import Papa from "papaparse";
import { normalizeHeader } from "./csvHelpers.js";

export type RawCsvRecord = Record<string, string | undefined>;

export type CsvRecord = {
  line: number;
  raw: RawCsvRecord;
  parseError: string | null;
};

export type CsvFieldsRecord = { line: number; fields: string[] };

export function readText(input: File | string): Promise<string> {
  if (typeof input === "string") return Promise.resolve(input);
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === "string" ? reader.result : "");
    reader.onerror = () => reject(reader.error ?? new Error("The file could not be read"));
    reader.readAsText(input);
  });
}

function countOccurrences(text: string, needle: string, from: number, to: number): number {
  let count = 0;
  let index = text.indexOf(needle, from);
  while (index !== -1 && index < to) {
    count += 1;
    index = text.indexOf(needle, index + needle.length);
  }
  return count;
}

export function readCsvRecords(input: string): { header: string[]; records: CsvFieldsRecord[] } {
  const text = input.replace(/^﻿/, "");
  const state: { header: string[] | null; fatal: Error | null } = { header: null, fatal: null };
  const records: CsvFieldsRecord[] = [];
  let recordStart = 0;
  let scanned = 0;
  let line = 1;

  Papa.parse<string[]>(text, {
    header: false,
    delimiter: ",",
    skipEmptyLines: false,
    step: (result, parser) => {
      const newline = result.meta.linebreak === "\r" ? "\r" : "\n";
      line += countOccurrences(text, newline, scanned, recordStart);
      scanned = recordStart;
      recordStart = result.meta.cursor;
      const [error] = result.errors;
      if (error) {
        state.fatal = new Error(`CSV could not be parsed: ${error.message} (line ${line})`);
        parser.abort();
        return;
      }
      if (state.header === null) {
        state.header = result.data.map(normalizeHeader);
        return;
      }
      records.push({ line, fields: result.data });
    },
  });

  if (state.fatal) throw state.fatal;
  if (state.header === null) throw new Error("CSV is empty");
  return { header: state.header, records };
}

export function fieldCountProblem(header: string[], fields: string[]): string | null {
  if (fields.length > header.length) {
    return `Too many fields: expected ${header.length} fields but parsed ${fields.length}`;
  }
  if (fields.length < header.length) {
    return `Too few fields: expected ${header.length} fields but parsed ${fields.length}`;
  }
  return null;
}

function missingColumns(header: string[], required: readonly string[]): string[] {
  const present = new Set(header);
  return required.filter(column => !present.has(column));
}

export async function parseCsvRecords(
  input: File | string,
  requiredColumns: readonly string[],
): Promise<CsvRecord[]> {
  const { header, records } = readCsvRecords(await readText(input));
  const missing = missingColumns(header, requiredColumns);
  if (missing.length > 0) {
    throw new Error(
      `CSV is missing required column(s): ${missing.join(", ")}. Expected header: ${requiredColumns.join(",")}`,
    );
  }
  return records.flatMap(({ line, fields }) => {
    if (fields.every(value => value.trim() === "")) return [];
    const raw: RawCsvRecord = {};
    header.forEach((column, index) => {
      raw[column] = fields[index];
    });
    return [{ line, raw, parseError: fieldCountProblem(header, fields) }];
  });
}
